import { Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { extractBalancedJsonObject } from '../utils/markup-string-scan.util';
import { escapeHtmlAttribute } from './ref-link.utils';
import { guid } from '@progress/kendo-angular-common';

export const WEB_SEARCH_REF_CLASS = 'web-search-ref';
export const WEB_SEARCH_TYPE = 'web_search' as const;

const WEB_SEARCH_START_PATTERN = new RegExp(
  String.raw`\{\s*"type"\s*:\s*"${WEB_SEARCH_TYPE}"`,
  'g',
);

export interface WebSearchCitation {
  type: typeof WEB_SEARCH_TYPE;
  title: string;
  summary: string;
  date?: string | null;
  url: string;
}

type RawWebSearchCitation = Partial<WebSearchCitation> & {
  link?: string;
  citation?: string;
  snippet?: string;
  publishedDate?: string | null;
};

interface WebSearchCitationSpan {
  start: number;
  end: number;
  citation: WebSearchCitation;
}

function truncateText(value: string, maxLength: number): string {
  if (value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, maxLength - 1)}…`;
}

function parseWebSearchCitation(raw: string): WebSearchCitation | null {
  try {
    const parsed = JSON.parse(raw) as RawWebSearchCitation;
    const url = parsed.url ?? parsed.link ?? parsed.citation;
    const summary = parsed.summary ?? parsed.snippet;
    const date = parsed.date ?? parsed.publishedDate;
    if (
      parsed.type !== WEB_SEARCH_TYPE ||
      typeof parsed.title !== 'string' ||
      typeof summary !== 'string' ||
      typeof url !== 'string'
    ) {
      return null;
    }

    return {
      type: WEB_SEARCH_TYPE,
      title: parsed.title,
      summary,
      url,
      date: date ?? undefined,
    };
  } catch {
    return null;
  }
}

export function findWebSearchCitationSpans(
  value: string,
): WebSearchCitationSpan[] {
  const spans: WebSearchCitationSpan[] = [];
  WEB_SEARCH_START_PATTERN.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = WEB_SEARCH_START_PATTERN.exec(value)) !== null) {
    const start = match.index;
    const raw = extractBalancedJsonObject(value, start);
    if (!raw) {
      continue;
    }

    const citation = parseWebSearchCitation(raw);
    if (!citation) {
      continue;
    }

    spans.push({
      start,
      end: start + raw.length - 1,
      citation,
    });
    WEB_SEARCH_START_PATTERN.lastIndex = start + raw.length;
  }

  return spans;
}

export function getWebSearchCitationLabel(title: string, url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./i, '');
  } catch {
    return truncateText(title, 28);
  }
}

/**
 * The hover dialog of a web citation: date, summary and a link to the page. Shared by the classic
 * chips and the v2 markers; the anchor name positions it under its chip or marker.
 */
export function renderWebSearchPopup(
  citation: Pick<WebSearchCitation, 'summary' | 'url' | 'date'>,
  openSourceLabel: string,
  anchorName: string,
): string {
  const summary = escapeHtmlAttribute(citation.summary);
  const url = escapeHtmlAttribute(citation.url);
  const date = escapeHtmlAttribute(citation.date ?? '');
  const openLabel = escapeHtmlAttribute(openSourceLabel);
  const popupStyle = `position-anchor: ${anchorName};`;
  const dateLine = citation.date
    ? `<span class="block text-caption text-neutral-70 mb-1">${date}</span>`
    : '';

  return `
      <span
        class="web-search-ref-popup block w-72 max-w-sm p-3 rounded shadow-card border border-neutral-20 bg-white text-left fixed z-[9999] isolate [contain:layout_style] bottom-[calc(anchor(top)+0.5rem)] left-[anchor(center)] -translate-x-1/2 opacity-0 invisible transition-[opacity,visibility] duration-150 delay-300 after:absolute after:left-0 after:right-0 after:top-full after:h-2 after:content-[''] group-hover:delay-0 group-hover:opacity-100 group-hover:visible group-focus-within:delay-0 group-focus-within:opacity-100 group-focus-within:visible hover:delay-0 hover:opacity-100 hover:visible"
        style="${popupStyle}"
        role="tooltip"
      >
        ${dateLine}
        <span class="block text-body-2 text-neutral-90 whitespace-pre-wrap max-h-48 overflow-y-auto mb-2">${summary}</span>
        <a class="web-search-ref-open block text-caption text-finserv-100 font-medium hover:underline" href="${url}" target="_blank" rel="noopener noreferrer">${openLabel}</a>
      </span>`;
}

export function renderWebSearchCitationChip(
  citation: WebSearchCitation,
  openSourceLabel: string,
): string {
  const label = escapeHtmlAttribute(
    getWebSearchCitationLabel(citation.title, citation.url),
  );
  const title = escapeHtmlAttribute(citation.title);

  const anchorName = `--web-search-ref-anchor-${guid()}`;
  const wrapperStyle = `anchor-name: ${anchorName};`;

  return `
    <span class="web-search-ref-wrapper group relative isolate" style="${wrapperStyle}">
      <button
        type="button"
        class="${WEB_SEARCH_REF_CLASS} inline-flex max-w-[14rem] items-center align-middle mx-0.5 px-2 py-0.5 rounded-full border border-neutral-20 bg-neutral-5 text-caption text-finserv-100 font-medium cursor-default hover:bg-bush-green-15 hover:border-neutral-40 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-finserv-100"
        aria-label="${title}"
      ><span class="truncate pointer-events-none">${label}</span></button>${renderWebSearchPopup(citation, openSourceLabel, anchorName)}
    </span>
  `;
}

@Pipe({ name: 'webSearchRef' })
export class WebSearchRefPipe implements PipeTransform {
  constructor(private readonly translate: TranslateService) {}

  /**
   * `enabled` is off while an answer streams: a citation whose JSON is still arriving would be
   * rendered from a half-written object.
   */
  transform(value?: string | null, enabled = true): string {
    if (!value) {
      return '';
    }

    if (!enabled) {
      return value;
    }

    const spans = findWebSearchCitationSpans(value);

    if (spans.length === 0) {
      return value;
    }

    let result = value;
    for (let i = spans.length - 1; i >= 0; i--) {
      const { start, end, citation } = spans[i];
      result =
        result.slice(0, start) +
        renderWebSearchCitationChip(
          citation,
          this.translate.instant('iqboost.webSearchCitation.openSource'),
        ) +
        result.slice(end + 1);
    }

    return result;
  }
}
